import React, { useState } from "react";
import axios from "axios";
import { Briefcase, Phone, Check, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HiringSection = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    position: "",
    experience: "",
    availability: "",
    about: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim() || !form.position) {
      toast.error("Please fill out the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/career-application`, form);
      toast.success("Application received! We'll be in touch soon.");
      setForm({
        full_name: "",
        phone: "",
        email: "",
        position: "",
        experience: "",
        availability: "",
        about: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please call us at (936) 275-0222.");
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Competitive pay with regular reviews",
    "Flexible scheduling — including PRN",
    "Training provided for the right person",
    "A supportive team that feels like family",
    "Make a real difference — every shift",
  ];

  return (
    <>
      {/* Deep purple: Care for people who care for everyone else */}
      <section id="careers" className="purple-section" data-testid="hiring-benefits-section">
        <div className="container-hc py-20 md:py-28">
          <div className="hc-chip chip-purple-on-dark" data-testid="were-hiring-chip">
            <Briefcase size={14} />
            We're Hiring
          </div>

          <h2 className="font-serif-display mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-4xl">
            Care for people who care for everyone else.
          </h2>

          <p className="mt-7 max-w-3xl text-lg text-white/75 leading-relaxed">
            Working at Hillcrest isn't a job — it's a calling. If you have a heart for serving
            people, a desire to make someone's day brighter, and a willingness to show up — we
            want to talk to you. Experience helps, but a good heart matters more.
          </p>

          <div className="mt-10 space-y-3 max-w-3xl">
            {benefits.map((b) => (
              <div key={b} className="flex items-center gap-4 text-white text-lg" data-testid={`benefit-${b.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>
                <span className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-[#a8c8e8] flex-shrink-0">
                  <Check size={14} className="text-[#a8c8e8]" strokeWidth={3} />
                </span>
                {b}
              </div>
            ))}
          </div>

          <div className="mt-10 bg-white/[0.06] border border-white/15 rounded-2xl px-6 py-5 max-w-3xl">
            <p className="text-white/85 leading-relaxed">
              <strong className="text-white">Prefer to apply in person?</strong> Stop by Mon — Sat,
              9 AM — 5 PM. We'd love to meet you.
            </p>
          </div>

          {/* Application form */}
          <form
            id="apply"
            onSubmit={submit}
            className="mt-12 bg-white rounded-[28px] p-8 md:p-10 shadow-xl"
            data-testid="career-form"
          >
            <h3 className="font-serif-display text-3xl md:text-4xl text-[color:var(--hc-navy)]">
              Apply to Join Our Team
            </h3>
            <p className="mt-3 text-[color:var(--hc-navy)]/65">
              We're always looking for caring people. No experience? We train.
            </p>

            <div className="mt-7 space-y-4">
              <input
                className="hc-input"
                placeholder="Full Name *"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
                data-testid="career-input-name"
              />
              <input
                className="hc-input"
                placeholder="Phone Number *"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                data-testid="career-input-phone"
              />
              <input
                type="email"
                className="hc-input"
                placeholder="Email Address *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="career-input-email"
              />
              <select
                className="hc-input appearance-none"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                required
                data-testid="career-input-position"
              >
                <option value="">Position you're applying for *</option>
                <option value="cna">Certified Nursing Assistant (CNA)</option>
                <option value="caregiver">Caregiver</option>
                <option value="lvn">LVN / LPN</option>
                <option value="rn">Registered Nurse (RN)</option>
                <option value="med-aide">Medication Aide</option>
                <option value="cook">Cook / Dietary</option>
                <option value="housekeeping">Housekeeping</option>
                <option value="activities">Activities Director</option>
                <option value="other">Other</option>
              </select>
              <select
                className="hc-input appearance-none"
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
                data-testid="career-input-experience"
              >
                <option value="">Years of experience</option>
                <option value="none">No experience — willing to learn</option>
                <option value="0-1">Less than 1 year</option>
                <option value="1-3">1 — 3 years</option>
                <option value="3-5">3 — 5 years</option>
                <option value="5-10">5 — 10 years</option>
                <option value="10+">10+ years</option>
              </select>
              <input
                className="hc-input"
                placeholder="Availability (e.g., 'Days, weekends, full-time')"
                value={form.availability}
                onChange={(e) => setForm({ ...form, availability: e.target.value })}
                data-testid="career-input-availability"
              />
              <textarea
                className="hc-textarea"
                placeholder="Tell us a little about yourself and why you want to work at Hillcrest."
                value={form.about}
                onChange={(e) => setForm({ ...form, about: e.target.value })}
                data-testid="career-input-about"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 btn-coral w-full justify-center disabled:opacity-60"
              data-testid="career-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Briefcase size={18} />}
              {loading ? "Submitting..." : "Submit Application"}
            </button>

            <p className="mt-5 text-xs text-[color:var(--hc-navy)]/55 text-center leading-relaxed">
              Hillcrest is an Equal Opportunity Employer. We celebrate diversity and are committed
              to creating an inclusive environment for all employees.
            </p>
          </form>
        </div>
      </section>
    </>
  );
};

export default HiringSection;
